/*!
 * vue-dropzone v0.10.9 
 * (c) 2020 Alex Tkachenko
 * Released under the MIT License.
 */
import { Dropzone } from '@aspectus/dropzone';
import { createBlockGenerator, createStateGenerator } from '@aspectus/bem';
import { StylingMixin } from '@aspectus/vue-bem-styling';

//
var name = 'vue-dropzone';
/**
 * File upload trigger component.
 *
 * @author [Alex Tkachenko](https://github.com/preusx)
 */

var script = {
  name: name,
  mixins: [StylingMixin],
  props: {
    blockName: {
      type: String,
      default: name
    },
    multiple: {
      type: Boolean,
      default: false
    },
    config: Object
  },
  data: function data() {
    this.dropzone = new Dropzone();
    this.configure();
    return {
      dragging: false
    };
  },
  watch: {
    config: {
      handler: 'configure'
    }
  },
  computed: {
    block: function block() {
      return createBlockGenerator()(this.blockName);
    }
  },
  mounted: function mounted() {
    this.dropzone.bind([this.$refs.body], [this.$refs.input]);
    this.dropzone.emitter.on('drag-start', this.dragStart);
    this.dropzone.emitter.on('drag-stop', this.dragStop);
    this.dropzone.emitter.on('drop', this.drop);
  },
  destroyed: function destroyed() {
    this.dropzone.unbind();
  },
  methods: {
    /**
     * Opens file upload dialog.
     * @public
     */
    open: function open() {
      this.$refs.input.click();
    },
    dragStart: function dragStart() {
      this.dragging = true;
    },
    dragStop: function dragStop() {
      this.dragging = false;
    },
    drop: function drop(event) {
      this.$emit('drop', event);
    },
    configure: function configure() {
      var config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.config;
      this.dropzone.configure(config);
    },
    state: createStateGenerator()
  }
};

function normalizeComponent(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier
/* server only */
, shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
  if (typeof shadowMode !== 'boolean') {
    createInjectorSSR = createInjector;
    createInjector = shadowMode;
    shadowMode = false;
  } // Vue.extend constructor export interop.


  var options = typeof script === 'function' ? script.options : script; // render functions

  if (template && template.render) {
    options.render = template.render;
    options.staticRenderFns = template.staticRenderFns;
    options._compiled = true; // functional template

    if (isFunctionalTemplate) {
      options.functional = true;
    }
  } // scopedId


  if (scopeId) {
    options._scopeId = scopeId;
  }

  var hook;

  if (moduleIdentifier) {
    // server build
    hook = function hook(context) {
      // 2.3 injection
      context = context || // cached call
      this.$vnode && this.$vnode.ssrContext || // stateful
      this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext; // functional
      // 2.2 with runInNewContext: true

      if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
        context = __VUE_SSR_CONTEXT__;
      } // inject component styles


      if (style) {
        style.call(this, createInjectorSSR(context));
      } // register component module identifier for async chunk inference


      if (context && context._registeredComponents) {
        context._registeredComponents.add(moduleIdentifier);
      }
    }; // used by ssr in case component is cached and beforeCreate
    // never gets called


    options._ssrRegister = hook;
  } else if (style) {
    hook = shadowMode ? function (context) {
      style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot));
    } : function (context) {
      style.call(this, createInjector(context));
    };
  }

  if (hook) {
    if (options.functional) {
      // register for functional component in vue file
      var originalRender = options.render;

      options.render = function renderWithStyleInjection(h, context) {
        hook.call(context);
        return originalRender(h, context);
      };
    } else {
      // inject component registration as beforeCreate hook
      var existing = options.beforeCreate;
      options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
    }
  }

  return script;
}

var isOldIE = typeof navigator !== 'undefined' && /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());

/* script */
const __vue_script__ = script;

/* template */
var __vue_render__ = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "tag",
    _vm._g(
      _vm._b(
        { class: [_vm.block(_vm.modifiers), _vm.state(_vm.states)] },
        "tag",
        _vm.$attrs,
        false
      ),
      _vm.$listeners
    ),
    [
      _c("input", {
        ref: "input",
        class: _vm.block("input"),
        attrs: { type: "file", multiple: _vm.multiple }
      }),
      _vm._v(" "),
      _c(
        "div",
        { ref: "body", class: _vm.block("body") },
        [
          _vm._t("default", null, { open: _vm.open, block: _vm.block }),
          _vm._v(" "),
          _vm.dragging ? _vm._t("cover") : _vm._e()
        ],
        2
      )
    ]
  )
};
var __vue_staticRenderFns__ = [];
__vue_render__._withStripped = true;

  /* style */
  const __vue_inject_styles__ = undefined;
  /* scoped */
  const __vue_scope_id__ = undefined;
  /* module identifier */
  const __vue_module_identifier__ = undefined;
  /* functional template */
  const __vue_is_functional_template__ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = normalizeComponent(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  );

function install(Vue) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref$name = _ref.name,
      name = _ref$name === void 0 ? __vue_component__.name : _ref$name;

  Vue.component(name, __vue_component__);
}

var main = {
  install: install
};

export default main;
export { __vue_component__ as Dropzone };
